import getClassNames from 'classnames';
import React from 'react';
import classNames from 'classnames';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { CommonProps } from '../Element/Element';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import Icon from '../Icon/Icon';
import ButtonText from '../Typography/ButtonText/ButtonText';
import styles from './Button.module.scss';
import Loader from './../Loader/Loader';
import BodyText from '../Typography/BodyText/BodyText';
import { SupportedColor, SupportedFontWeight } from '../Element/Element';
import { SupportedIconName } from '../../shared/icons';
import { CreateProps } from '../../types/utils/CreateProps';

const iconSizeByButtonSize = {
  sm: 'xs',
  md: 'sm',
  lg: 'sm',
  xl: 'sm',
} as const;

const textButtonSizeByButtonSize = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
  xl: 'md',
} as const;
const horizontalPaddingSizeByButtonSize = {
  sm: 'sm',
  md: 'md',
  lg: 'md',
  xl: 'md',
} as const;

type ButtonProps = CreateProps<
  {
    /** Button variant */
    variant?: SupportedButtonVariant;
    /** Set button size */
    size?: SupportedButtonSize;
    /** Set button icon */
    iconName?: SupportedIconName;
    /** Toggle button to be disabled */
    disabled?: boolean;
    /** Triggers loading state */
    loading?: boolean;
    /** Toggle button type */
    type?: 'submit' | 'button' | 'reset';
    /** Is icon placement right, defaults to left */
    iconOnRight?: boolean;
    /** Toggle possibility of button to take all available width in mobile view */
    expandWhenMobile?: boolean;
    /** These props only apply to text variant */
    textVariantProps?: {
      color?: SupportedColor;
      fontWeight?: SupportedFontWeight;
      hoverColor?: SupportedColor;
    };
  },
  CommonProps<'button'>,
  'color' | 'borderRadius' | 'hoverColor'
>;

const Button: React.FunctionComponent<ButtonProps> = ({
  loading = false,
  children,
  iconName,
  className,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  type,
  iconOnRight = false,
  expandWhenMobile,
  textVariantProps = {
    color: 'black',
    hoverColor: 'purple500',
  },
  ...props
}) => {
  const sizeLowerCase = size.toLowerCase() as SupportedButtonSize;
  const isText = variant === 'text';

  const resolveGutter = () => {
    if (isText && iconName) {
      return <Gutter quarter />;
    }
    return size === 'sm' ? <Gutter half /> : <Gutter size="sm" />;
  };

  const elementClassNames = getClassNames(
    styles.button,
    styles[`button__${variant}`],
    {
      [styles.button__disabled]: disabled,
    },
    {
      [styles.button__expandWhenMobile]: expandWhenMobile,
    },
    {
      [styles.button__loading]: loading,
    },
    className
  );

  const getFlexClassName = () =>
    isText ? 'button__size__md' : styles[`button__size__${sizeLowerCase}`];

  const padding = {
    horizontal: horizontalPaddingSizeByButtonSize[sizeLowerCase],
  };

  return (
    <Flex
      centered
      alignBaseline={isText}
      reverseDirection={iconOnRight}
      padding={isText ? undefined : padding}
      tagName="button"
      className={classNames([elementClassNames, getFlexClassName()])}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
        !loading && !disabled && onClick && onClick(e)
      }
      disabled={disabled === true}
      type={type}
      color={isText && disabled ? 'gray400' : textVariantProps.color}
      borderRadius={isText ? undefined : { size: 'rounded' }}
      hoverColor={isText && !disabled ? textVariantProps.hoverColor : undefined}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault();
      }}
      {...props}
    >
      {loading ? (
        <Loader
          className={styles.button__loader}
          light={loaderColor[variant]}
          size="sm"
          aria-label="Loading"
          aria-busy="true"
        ></Loader>
      ) : (
        iconName && (
          <Icon
            name={iconName}
            size={isText ? 'xs' : iconSizeByButtonSize[sizeLowerCase]}
            className={styles.button__icon}
            aria-hidden="true"
          />
        )
      )}

      {
        // Adjust the gutter while loading or if the button has an icon
        (loading || iconName) && resolveGutter()
      }

      {isText ? ( // use BodyText with text variant
        <BodyText
          tagName="span"
          size={textButtonSizeByButtonSize[sizeLowerCase]}
          singleLine
          fontWeight={textVariantProps.fontWeight}
        >
          {children}
        </BodyText>
      ) : (
        <ButtonText size={sizeLowerCase === 'xl' ? 'md' : 'sm'}>
          {children}
        </ButtonText>
      )}
    </Flex>
  );
};

const loaderColor = {
  primary: true,
  purchase: true,
  purchaseSecondary: false,
  secondary: false,
  primaryWhite: false,
  secondaryWhite: true,
  destructive: true,
  secondaryDestructive: false,
  text: false,
} as const;

export const supportedButtonSizes = ['sm', 'md', 'lg', 'xl'] as const;

export const supportedButtonVariants = [
  'primary',
  'purchase',
  'secondary',
  'primaryWhite',
  'purchaseSecondary',
  'secondaryWhite',
  'destructive',
  'secondaryDestructive',
  'text',
] as const;

export type SupportedButtonVariant = ArrayElementType<
  typeof supportedButtonVariants
>;
type SupportedButtonSize = ArrayElementType<typeof supportedButtonSizes>;
// Aliases for backwards compatibility
export type ButtonVariant = SupportedButtonVariant;
export type ButtonAttributes = ButtonProps;

export default Button;
